import { Link } from "react-router-dom";
import { CASES } from "@/lib/data";
import { useReveal } from "@/hooks/useReveal";

export const CaseCard = ({ item, index = 0, featured = false }) => {
  const ref = useReveal();
  return (
    <article
      ref={ref}
      data-testid={`case-card-${item.slug}`}
      className={`reveal case-card group relative overflow-hidden rounded-3xl border border-white/10 bg-[hsl(var(--bg-card))] hover:border-[hsl(var(--accent))]/60 transition-all duration-500 ${
        featured ? "lg:col-span-2" : ""
      }`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <Link to={`/cases/${item.slug}`} className="block">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={item.image}
            alt={`${item.name} case study`}
            className="case-img absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute top-5 left-5 flex flex-wrap gap-2">
            {item.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white/85"
              >
                {t}
              </span>
            ))}
          </div>
          <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
            <div>
              <div className="text-xs text-[hsl(var(--accent))] uppercase tracking-widest font-semibold">{item.category}</div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold mt-1.5">{item.name}</h3>
            </div>
          </div>
        </div>

        <div className="p-7 lg:p-8">
          <h4 className="font-display text-xl sm:text-2xl font-semibold leading-snug">
            {item.headline}
          </h4>
          <p className="mt-3 text-white/65 leading-relaxed text-sm sm:text-base">
            {item.description}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-sm text-[hsl(var(--accent))] font-semibold group-hover:gap-3 transition-all">
            Læs hele casen
            <span aria-hidden>→</span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export const Cases = ({ limit, showCta = true, hideHeading = false }) => {
  const titleRef = useReveal();
  const list = limit ? CASES.slice(0, limit) : CASES;
  return (
    <section id="cases" className="py-12 lg:py-16 relative">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        {!hideHeading && (
          <div ref={titleRef} className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--accent))] font-semibold">
                Udvalgte Cases
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 max-w-3xl leading-[1.05]">
                Virksomheder vi har{" "}
                <span className="italic font-light text-white/60">løftet.</span>
              </h2>
            </div>
            <p className="text-white/60 max-w-md">
              Et udvalg af de virksomheder vi har hjulpet med synlighed, content og vækst online.
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {list.map((c, i) => (
            <CaseCard key={c.slug} item={c} index={i} featured={i % 3 === 0} />
          ))}
        </div>

        {showCta && limit && (
          <div className="mt-10 text-center">
            <Link
              to="/cases"
              className="btn-pop inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white/90 hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]"
            >
              Se alle cases
              <span aria-hidden>→</span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
